import React from 'react';
import { Logo } from '@dopomogai/ui';
import { Clock, BarChart3, CheckCircle2, Link2, ArrowRight, ShieldCheck, MessageCircle } from 'lucide-react';

export type CVBadges = {
  Role?: string;
  Family?: string;
  Platforms?: string;
  Tools?: string;
  Channels?: string;
  Languages?: string;
  Availability?: string;
  Impact?: string;
};

export type CVKPIs = {
  conversionLiftPp?: string;     // "+0.4–0.8 pp"
  supportReductionPct?: string;  // "−20–40%"
  firstResponseTime?: string;    // "0.5–2 s"
  meetingsBookedPct?: string;    // sales
  roasUpliftPct?: string;        // marketing
};

export type CVTimelineStep =
  | string
  | { title: string; detail?: string; days?: string };

export interface AgentCVData {
  title: string;
  subtitle?: string;
  aboutMe: string;               // first-person narrative
  badges: CVBadges;
  personaMedia?: {
    imageUrl?: string;
    videoWebm?: string;
    videoMp4?: string;
    posterUrl?: string;
    alt?: string;
  };
  highlights?: string[];
  capabilities: string[];
  integrations: string[];
  kpis?: CVKPIs;
  socialImpact?: string;

  // Setup
  setup?: {
    starter?: {
      title?: string;            // "Starter setup (base install)"
      price?: string;            // "$300–$600"
      eta?: string;              // "3–5 business days"
      includes?: string[];
    };
  };
  setupPricing?: {
    typical?: string;            // "from $3,000"
    range?: string;              // "$1,500–$15,000"
    eta?: string;                // "2–6 weeks (platform approvals may add 1–3 weeks)"
  };
  timeline?: CVTimelineStep[];   // phase breakdown (strings or objects)
  addons?: string[];             // Popular add-ons

  demoUrl?: string;
  contactUrl?: string;
  qrImageUrl?: string;
  accentHex?: string;            // category accent color
}

type Props = {
  data: AgentCVData;
  className?: string;
};

const Tag: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="inline-flex items-center gap-1 border-2 border-black rounded-full px-2 py-0.5 text-xs bg-white">
    <CheckCircle2 className="w-3 h-3" /> {children}
  </span>
);

const StatCard: React.FC<{ label: string; value: string; icon?: React.ReactNode }> = ({ label, value, icon }) => (
  <div className="border-2 border-black rounded-lg p-3 bg-slate-50 flex items-center gap-2">
    {icon}
    <div className="text-sm">
      <div className="font-extrabold">{value}</div>
      <div className="text-slate-600">{label}</div>
    </div>
  </div>
);

export const AgentCVDocument = React.forwardRef<HTMLDivElement, Props>(function AgentCVDocument(
  { data, className },
  ref
) {
  const accent = data.accentHex || '#10B981';
  const hasVideo = !!(data.personaMedia?.videoWebm || data.personaMedia?.videoMp4);

  const renderTimelineItem = (item: CVTimelineStep, idx: number) => {
    if (typeof item === 'string') {
      return (
        <li key={idx} className="flex items-start gap-2 text-sm">
          <ArrowRight className="w-4 h-4 mt-0.5" />
          <div>{item}</div>
        </li>
      );
    }
    return (
      <li key={idx} className="flex items-start gap-2 text-sm">
        <ArrowRight className="w-4 h-4 mt-0.5" />
        <div>
          <b>{item.title}</b>
          {item.days && <span className="text-slate-600"> — {item.days}</span>}
          {item.detail && <div className="text-slate-700">{item.detail}</div>}
        </div>
      </li>
    );
  };

  return (
    <div
      ref={ref}
      className={`w-[210mm] min-h-[297mm] mx-auto bg-white text-slate-900 flex flex-col p-10 md:p-12 font-sans border-2 border-black print:shadow-none ${className || ''}`}
      style={{ boxShadow: '10px 10px 0 rgba(0,0,0,0.18)' }}
    >
      {/* Header (no logo) */}
      <header className="flex items-start justify-between pb-4 gap-6" id="overview">
        <div className="flex items-start gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide">
              <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: accent }} />
              AI employee CV
            </div>
            <h1 className="text-2xl md:text-3xl font-black leading-tight mt-1">{data.title}</h1>
            {data.subtitle && <p className="text-sm text-slate-700 mt-1">{data.subtitle}</p>}
            <div className="flex flex-wrap gap-2 mt-3">
              {Object.entries(data.badges || {}).map(([key, val]) =>
                val ? (
                  <span key={key} className="border-2 border-black rounded-lg px-2 py-1 text-xs bg-white">
                    <span className="font-semibold">{key}:</span> {val}
                  </span>
                ) : null
              )}
            </div>
          </div>
        </div>

        {(hasVideo || data.personaMedia?.imageUrl) && (
          <div
            className="border-2 border-black rounded-xl overflow-hidden shrink-0 bg-white"
            style={{ width: 160, height: 160, boxShadow: '6px 6px 0 rgba(0,0,0,0.15)' }}
          >
            {hasVideo ? (
              <video autoPlay muted loop playsInline poster={data.personaMedia?.posterUrl} className="w-full h-full object-cover">
                {data.personaMedia?.videoWebm && <source src={data.personaMedia.videoWebm} type="video/webm" />}
                {data.personaMedia?.videoMp4 && <source src={data.personaMedia.videoMp4} type="video/mp4" />}
              </video>
            ) : (
              <img src={data.personaMedia?.imageUrl} alt={data.personaMedia?.alt || 'Agent portrait'} className="w-full h-full object-cover" />
            )}
          </div>
        )}
      </header>

      {/* About me + highlights + KPIs */}
      <section className="mt-6">
        <div className="border-2 border-black rounded-xl p-4 bg-white" style={{ boxShadow: '6px 6px 0 rgba(0,0,0,0.15)' }}>
          <h3 className="text-lg font-extrabold mb-2">About me</h3>
          <p className="text-base leading-relaxed whitespace-pre-line">{data.aboutMe}</p>

          {data.highlights && data.highlights.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {data.highlights.map((h, i) => (
                <Tag key={i}>{h}</Tag>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
            {data.kpis?.conversionLiftPp && (
              <StatCard label="Conversion uplift" value={data.kpis.conversionLiftPp} icon={<BarChart3 className="w-4 h-4" />} />
            )}
            {data.kpis?.firstResponseTime && (
              <StatCard label="First response" value={data.kpis.firstResponseTime} icon={<Clock className="w-4 h-4" />} />
            )}
            {data.kpis?.supportReductionPct && (
              <StatCard label="Ticket reduction" value={data.kpis.supportReductionPct} icon={<ShieldCheck className="w-4 h-4" />} />
            )}
            {data.kpis?.meetingsBookedPct && (
              <StatCard label="Meetings booked" value={data.kpis.meetingsBookedPct} icon={<MessageCircle className="w-4 h-4" />} />
            )}
            {data.kpis?.roasUpliftPct && (
              <StatCard label="ROAS uplift" value={data.kpis.roasUpliftPct} icon={<BarChart3 className="w-4 h-4" />} />
            )}
          </div>

          {data.socialImpact && <p className="text-sm text-slate-600 mt-3">{data.socialImpact}</p>}
        </div>
      </section>

      {/* Capabilities / Integrations */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <section className="border-2 border-black rounded-xl p-4 bg-slate-50" id="capabilities" style={{ boxShadow: '6px 6px 0 rgba(0,0,0,0.12)' }}>
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-block w-1.5 h-5 rounded-full" style={{ backgroundColor: accent }} />
            <h3 className="text-lg font-extrabold">What I do best</h3>
          </div>
          <ul className="list-disc pl-5 space-y-1 text-sm">
            {data.capabilities.map((c, idx) => (
              <li key={idx}>{c}</li>
            ))}
          </ul>
        </section>

        <section className="border-2 border-black rounded-xl p-4 bg-slate-50" id="integrations" style={{ boxShadow: '6px 6px 0 rgba(0,0,0,0.12)' }}>
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-block w-1.5 h-5 rounded-full" style={{ backgroundColor: accent }} />
            <h3 className="text-lg font-extrabold">How I plug into your stack</h3>
          </div>
          <ul className="list-disc pl-5 space-y-1 text-sm">
            {data.integrations.map((it, idx) => (
              <li key={idx}>{it}</li>
            ))}
          </ul>
        </section>
      </section>

      {/* Setup & timeline + Add-ons */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <section className="border-2 border-black rounded-xl p-4 bg-white" id="setup-pricing" style={{ boxShadow: '6px 6px 0 rgba(0,0,0,0.12)' }}>
          <h3 className="text-lg font-extrabold mb-2">Setup, pricing, and timeline</h3>

          {data.setup?.starter && (
            <div className="border-2 border-black rounded-lg p-3 bg-slate-50 text-sm">
              <b>{data.setup.starter.title || 'Starter setup (base install)'}:</b>{' '}
              {data.setup.starter.price || '$300–$600'} • {data.setup.starter.eta || '3–5 business days'}
              {data.setup.starter.includes && data.setup.starter.includes.length > 0 && (
                <ul className="list-disc pl-5 mt-2">
                  {data.setup.starter.includes.map((i, idx) => (
                    <li key={idx}>{i}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="space-y-1 text-sm mt-3">
            {data.setupPricing?.typical && (
              <p>
                Typical onboarding (tailored): <b>{data.setupPricing.typical}</b>
              </p>
            )}
            {data.setupPricing?.range && (
              <p>
                Range (feature‑based): <b>{data.setupPricing.range}</b>
              </p>
            )}
            {data.setupPricing?.eta && (
              <p>
                ETA (tailored): <b>{data.setupPricing.eta}</b>
              </p>
            )}
          </div>

          {data.timeline && data.timeline.length > 0 && (
            <ol className="mt-3 space-y-2">
              {data.timeline.map((step, i) => renderTimelineItem(step, i))}
            </ol>
          )}

          <p className="text-xs text-slate-600 mt-3">
            If approvals delay, I launch core chat/data first; socials/phone attach later—no extra cost.
          </p>
        </section>

        <section className="border-2 border-black rounded-xl p-4 bg-slate-50" id="addons" style={{ boxShadow: '6px 6px 0 rgba(0,0,0,0.12)' }}>
          <h3 className="text-lg font-extrabold mb-2">Popular add‑ons</h3>
          {data.addons && data.addons.length > 0 ? (
            <ul className="list-disc pl-5 space-y-1 text-sm">
              {data.addons.map((x, i) => (
                <li key={i}>{x}</li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-600">Choose add‑ons during scoping to fine‑tune impact and ETA.</p>
          )}
        </section>
      </section>

      {/* Footer with logo */}
      <footer className="mt-auto pt-4 border-t-2 border-black flex items-center justify-between" id="hire">
        <div className="flex items-center gap-3">
          <Logo logoType="logo+icon" size="sm" />
          <div className="text-sm text-slate-700 flex items-center gap-3 flex-wrap">
            {data.demoUrl ? (
              <a href={data.demoUrl} className="inline-flex items-center gap-1 text-emerald-700 font-semibold underline">
                <Link2 className="w-4 h-4" /> Live demo
              </a>
            ) : (
              <span>Live demo available on request</span>
            )}
            <span>•</span>
            {data.contactUrl ? (
              <a href={data.contactUrl} className="text-emerald-700 underline">
                Contact us
              </a>
            ) : (
              <span>contact@dpmg.xyz</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data.qrImageUrl && (
            <img src={data.qrImageUrl} alt="QR" className="border-2 border-black rounded-md" style={{ width: 56, height: 56 }} />
          )}
          <span className="border-2 border-black rounded-lg px-2 py-1 text-xs bg-slate-50">GDPR‑ready</span>
          <span className="border-2 border-black rounded-lg px-2 py-1 text-xs bg-slate-50">Built with our open builder</span>
        </div>
      </footer>
    </div>
  );
});

export default AgentCVDocument;
